import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { FullPageSpinner } from '@/design-system/components/FullPageSpinner';

import RootLayout from '@/routes/RootLayout';
import AuthLayout from '@/routes/auth/AuthLayout';
import LoginPage from '@/routes/auth/LoginPage';
import DeniedPage from '@/routes/auth/DeniedPage';
import NotFoundPage from '@/routes/NotFoundPage';
import { AuthGuard } from '@/features/auth/AuthGuard';
import { HomeRedirect } from '@/features/auth/HomeRedirect';

// User pages
import UserLayout from '@/routes/user/UserLayout';
import LobbyPage from '@/routes/user/LobbyPage';
import WaitingRoomPage from '@/routes/user/WaitingRoomPage';
import PlayPage from '@/routes/user/PlayPage';
import ResultPage from '@/routes/user/ResultPage';
import HistoryPage from '@/routes/user/HistoryPage';
import TournamentPage from '@/routes/user/TournamentPage';
import RpsRoomsPage from '@/routes/user/RpsRoomsPage';
import RpsRoomPage from '@/routes/user/RpsRoomPage';

// Admin pages (lazy)
const AdminLayout = lazy(() => import('@/routes/admin/AdminLayout'));
const EmployeesPage = lazy(() => import('@/routes/admin/EmployeesPage'));
const QuestionsPage = lazy(() => import('@/routes/admin/QuestionsPage'));
const RoundsListPage = lazy(() => import('@/routes/admin/RoundsListPage'));
const RoundEditPage = lazy(() => import('@/routes/admin/RoundEditPage'));
const RoundControlPage = lazy(() => import('@/routes/admin/RoundControlPage'));
const WordChainPage = lazy(() => import('@/routes/admin/WordChainPage'));

function LazyFallback() {
  return <FullPageSpinner />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public auth pages
      {
        element: <AuthLayout />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/denied', element: <DeniedPage /> },
        ],
      },

      // Home redirect
      {
        path: '/',
        element: (
          <AuthGuard>
            <HomeRedirect />
          </AuthGuard>
        ),
      },

      // User area (admin can also access)
      {
        element: (
          <AuthGuard>
            <UserLayout />
          </AuthGuard>
        ),
        children: [
          { path: '/lobby', element: <LobbyPage /> },
          { path: '/history', element: <HistoryPage /> },
          { path: '/rounds/:roundId/waiting', element: <WaitingRoomPage /> },
          { path: '/rounds/:roundId/play', element: <PlayPage /> },
          { path: '/rounds/:roundId/result', element: <ResultPage /> },
          { path: '/rounds/:roundId/tournament', element: <TournamentPage /> },
          { path: '/rps-rooms', element: <RpsRoomsPage /> },
          { path: '/rps-rooms/:roomId', element: <RpsRoomPage /> },
        ],
      },

      // Admin area
      {
        path: '/admin',
        element: (
          <AuthGuard role="admin">
            <Suspense fallback={<LazyFallback />}>
              <AdminLayout />
            </Suspense>
          </AuthGuard>
        ),
        children: [
          { index: true, element: <Navigate to="rounds" replace /> },
          {
            path: 'employees',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <EmployeesPage />
              </Suspense>
            ),
          },
          {
            path: 'questions',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <QuestionsPage />
              </Suspense>
            ),
          },
          {
            path: 'rounds',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <RoundsListPage />
              </Suspense>
            ),
          },
          {
            path: 'rounds/new',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <RoundEditPage mode="create" />
              </Suspense>
            ),
          },
          {
            path: 'rounds/:roundId/edit',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <RoundEditPage mode="edit" />
              </Suspense>
            ),
          },
          {
            path: 'rounds/:roundId/control',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <RoundControlPage />
              </Suspense>
            ),
          },
          {
            path: 'word-chain',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <WordChainPage />
              </Suspense>
            ),
          },
        ],
      },

      // 404
      { path: '*', element: <NotFoundPage /> },
    
    ],
  },
]);
